<script lang='ts'>
    import { xssDeviationToCategory, type XSSDetails, XSSDeviationCategory } from "@/Util/utils";
    import SimpleModal from "./SimpleModal.svelte";
    import XssDetailsTable from "./XSSDetailsTable.svelte";
    import type { FocusDescription } from "@/Common/constants";
    import { json } from 'svelte-i18n';
    import Skeleton from "@/Common/Skeleton.svelte";
    import { cn } from "@/lib/utils";
    import * as Popover from "$lib/components/ui/popover";
    import { Button } from "@/lib/components/ui/button";

    export let small: boolean = false
    export let thisXSSDetails: XSSDetails
    export let forecastedXSSDetails: XSSDetails | null = null
    export let skipDeviationClassCalculation: boolean = false
    export let isPlaceholder: boolean = false
    export let isMatched: boolean = false
    export let displayRemainingXSS: boolean = false
    export let focus: string
    export let specRating: string
    export let mainXSSDetailsTableTitle: string
    export let modalRight: boolean = false
    export let modalTight: boolean = false
    export let isStale: boolean = false

    const generateSpecificFocusDescription = (specificity: string, focus: string, xhss: number): [string, number | null] | null => {
        const sp = specificity.toLowerCase()
        const foc = focus.toLowerCase()

        const focusLocale = $json('focus')
        let details: FocusDescription | undefined = focusLocale[foc]

        if (!details) return null

        const specificityLocale = details.running ? $json('specificity_rating_running') : $json('specificity_rating')

        let spDesc: string | undefined = specificityLocale[sp]?.description

        if (!spDesc) return null
        
        if (details.endurance_alt_description && xhss != 0) {    
            details.description = details.endurance_alt_description
        } else if (details.endurance_alt_description && xhss == 0) {
            spDesc = ''
        }

        return [`${details.description} ${spDesc}`, details.minute_power]
    }

    let spfDesc: [string, number | null] | null = null
    $: if (isPlaceholder && specRating && focus) {
        spfDesc = generateSpecificFocusDescription(specRating, focus, thisXSSDetails.xhss)
    }

    const getXSSDeviationColours = (xss: XSSDetails, targetXSS: XSSDetails, skip: boolean): string => {
        const deviationUnkown = "bg-transparent border-[#707377]"
        if (skip || !xss || !targetXSS) return deviationUnkown

        switch (xssDeviationToCategory(xss, targetXSS).xss.suitability) {
            case XSSDeviationCategory.Good:
                return "bg-[var(--color-xss-deviation-good)] hover:bg-[var(--color-xss-deviation-good)] text-white hover:text-white"
            case XSSDeviationCategory.Warn:
                return "bg-[var(--color-xss-deviation-warn)] hover:bg-[var(--color-xss-deviation-warn)] text-white hover:text-white"
            case XSSDeviationCategory.Alert:
                return "bg-[var(--color-xss-deviation-alert)] hover:bg-[var(--color-xss-deviation-alert)] text-white hover:text-white"
            default:
                return deviationUnkown
        }
    }

</script>

{#if isStale}
    <Skeleton class={cn('rounded-full aspect-square shrink-0', small ? 'w-[50px]' : 'w-full')}/>
{:else}
    <Popover.Root portal="#shadcn-ui-dialog-target">
        <Popover.Trigger asChild let:builder>
            <Button
                variant='outline'
                class={cn(
                    'p-0 flex flex-col text-center shrink-0 rounded-full border-[2px] border-transparent border-solid transition-[filter] duration-[0.05s] hover:brightness-75',
                    small ? 'w-[50px] h-[50px]' : 'w-[100px] h-[100px]',
                    getXSSDeviationColours(thisXSSDetails, forecastedXSSDetails, skipDeviationClassCalculation),
                )}
                builders={[builder]}
                on:click={(e) => {
                    e.stopPropagation()
                }}
            >
                <h2 class={cn("font-bold my-0", small ? 'text-[1.1rem]' : '')}>{Math.round(thisXSSDetails.xss)}</h2>
                <h5 class={cn("mb-0 mt-1", small ? 'text-[0.6rem] m-0' : '')}>
                    XSS
                </h5>
            </Button>
        </Popover.Trigger>
        <Popover.Content side='top' align='center' class='w-auto'>
            <div class='max-w-[270px]'>
                <div class="flex flex-row gap-x-4 text-black">
                    {#if !isPlaceholder && forecastedXSSDetails}
                        <XssDetailsTable title={isMatched ? 'Scheduled' : 'Forecasted'} xssDetails={forecastedXSSDetails} />
                    {/if}
                    <div class='mx-auto'>
                        <XssDetailsTable title={mainXSSDetailsTableTitle} xssDetails={thisXSSDetails} targetXssDetails={forecastedXSSDetails} showRowNames={isPlaceholder || !forecastedXSSDetails} />
                    </div>
                    {#if isPlaceholder && specRating && focus}
                        <div class="py-2 w-max h-max m-auto">
                            <h4 class="my-0 mx-auto font-bold text-center">
                                {specRating + " " + focus}
                            </h4>
                            {#if spfDesc?.at(1)}
                                <p class="mb-0 mt-1 w-max mx-auto">({spfDesc?.at(1)} Minute Power)</p>
                            {/if}
                        </div>
                    {/if}
                </div>
                {#if isPlaceholder && spfDesc?.at(0)}
                    <p class="max-w-full text-center mb-0 !mt-1 mx-auto">
                        {spfDesc?.at(0)}
                    </p>
                {/if}
                {#if displayRemainingXSS && forecastedXSSDetails}
                    <p class="max-w-full text-center mb-0 !mt-1 mx-auto">
                        {Math.round(Math.max(0, forecastedXSSDetails.xss - thisXSSDetails.xss))} XSS Remaining
                    </p>
                {/if}
            </div>
        </Popover.Content>
    </Popover.Root>
{/if}

<style lang="scss">
    .difficulty {
        flex-shrink: 0;
        width: 100px;
        aspect-ratio: 1 / 1;
        text-align: center;
        border-radius: 9999px;
        transition: filter 0.05s;
        border: 2px solid transparent;
        &.small {
            width: 50px;
            h2 {
                font-size: 1.1rem;
            }
            h5 {
                margin: 0;
                font-size: 0.6rem;
            }
        }
        &:hover, &.detailsOpen {
            cursor: pointer;
            filter: brightness(75%);
            transition: filter 0.15s;
        }
        &.noTraining {
            height: min-content;
            &:hover {
                cursor: default;
                filter: brightness(100%);
            }
        }
    }
    .DeviationUnknown, .null {
        background-color: #f2f2f2;
        border: 2px solid #707377;
    }
    .DeviationGood {
        background-color: var(--color-xss-deviation-good);
        color: white;
    }
    .DeviationWarn {
        background-color: var(--color-xss-deviation-warn);
        color: white;
    }
    .DeviationAlert {
        background-color: var(--color-xss-deviation-alert);
        color: white;
    }
</style>
